import { useEffect, useState } from "react";

/**
 * Debounce a fast-changing value (e.g. a search input) so downstream effects
 * (queries, requests) only fire after the user pauses typing.
 *
 * Returns the debounced value. The first render returns the initial value
 * immediately; subsequent changes update `debounced` only after `ms` of
 * no further changes. Setting the value back to the current debounced value
 * (or unmount) cancels any pending timer.
 *
 * Used by the drug-search dropdowns (PrescriptionTable, EditMedicineDialog,
 * admin drugs page) so each keystroke past 3 chars doesn't fire a /drugs?q=
 * request — the request goes out once per typing pause (250ms), not per key.
 */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}